import React from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import { 
  IoArrowForwardOutline, 
  IoShieldCheckmarkOutline, 
  IoSearchOutline, 
  IoTimeOutline, 
  IoFingerPrintOutline, 
  IoPhonePortraitOutline, 
  IoListOutline, 
  IoRocketOutline, 
  IoSparklesOutline, 
  IoSyncOutline, 
  IoChatbubbleOutline, 
  IoExpandOutline, 
  IoGitCompareOutline,
  IoCloudOutline,
  IoLockClosedOutline,
  IoPeopleOutline,
  IoGridOutline,
  IoStatsChartOutline,
  IoNotificationsOutline,
  IoPieChartOutline,
  IoCopyOutline,
  IoKeyOutline,
  IoShieldOutline
} from 'react-icons/io5';
import dashboardImg from '../../../../assets/Images/dashboard.png';
import './ProductPage.css';

const ProductPage = () => {
  const { t } = useTranslation();

  const coreModules = [
    {
      icon: <IoListOutline />,
      title: "Intelligent Contract Repository & Smart Archiving",
      description: "Never lose track of a single agreement again. DocuSafe provides a centralized, high-performance repository that acts as the single source of truth for your organization's legal capital.",
      features: [
        { icon: <IoSearchOutline />, text: "AI-Powered OCR & Indexing: Every document, including scanned PDFs, is fully indexed and searchable in milliseconds." },
        { icon: <IoSyncOutline />, text: "Smart Metadata Tagging: Automatically categorize contracts by type, counterparty, and value using our AI engine." },
        { icon: <IoTimeOutline />, text: "Automated Milestone Alerts: Proactive notifications for renewals, termination windows, and obligations." },
        { icon: <IoLockClosedOutline />, text: "Hierarchical Governance: Secure, permission-based folders to ensure sensitive data stays protected." }
      ]
    },
    {
      icon: <IoFingerPrintOutline />,
      title: "Enterprise-Grade e-Signatures & Execution",
      description: "Speed up deal cycles with legally binding electronic signatures that work as fast as you do. Fully compliant with global regulations.",
      features: [
        { icon: <IoShieldCheckmarkOutline />, text: "Global Regulatory Compliance: Fully compliant with the ESIGN Act (USA), UETA, and eIDAS (EU)." },
        { icon: <IoPhonePortraitOutline />, text: "Seamless Mobile Experience: Execute documents on the go with a responsive signing interface—no app required." },
        { icon: <IoLockClosedOutline />, text: "Comprehensive Audit Trail: Tamper-proof certificates recording IP addresses and timestamps for evidentiary value." },
        { icon: <IoSyncOutline />, text: "Custom Signing Workflows: Set signing orders, add witnesses, and include dynamic fields effortlessly." }
      ]
    },
    {
      icon: <IoSparklesOutline />,
      title: "Contextual AI Intelligence & Automation",
      description: "Transform passive documents into active business intelligence with our advanced AI layer that works alongside your team.",
      features: [
        { icon: <IoRocketOutline />, text: "Automated Risk Scoring: Scan drafts to highlight high-risk clauses and potential financial bottlenecks." },
        { icon: <IoChatbubbleOutline />, text: "Intelligent Clause Library: Get AI-powered suggestions based on your historical negotiations." },
        { icon: <IoSyncOutline />, text: "Workflow Automation Engine: Route documents for approval automatically based on value or risk level." },
        { icon: <IoSearchOutline />, text: "Data Extraction: Automatically pull key data points from legacy contracts to populate your dashboard." }
      ]
    },
    {
      icon: <IoPeopleOutline />,
      title: "Real-Time Strategic Negotiation & Elite Collaboration",
      description: "Close deals faster by bringing all internal and external parties into a single, high-collaboration environment. Eliminate endless email threads.",
      features: [
        { icon: <IoChatbubbleOutline />, text: "In-Context Team Discussion: Use @mentions and internal comments to align legal, sales, and finance teams effortlessly." },
        { icon: <IoExpandOutline />, text: "Secure External Guest Portal: Invite counterparties to a branded, controlled environment to review, comment, and sign." },
        { icon: <IoGitCompareOutline />, text: "Dynamic Multi-Party Redlining: Real-time collaborative editing with side-by-side version comparison and history." },
        { icon: <IoSyncOutline />, text: "Advanced Approval Workflows: Define clear paths for internal consensus before external delivery." }
      ]
    },
    {
      icon: <IoCopyOutline />,
      title: "Smart Templates & Clause Library",
      description: "Standardize your legal language and accelerate contract creation with our intelligent templating engine.",
      features: [
        { icon: <IoSyncOutline />, text: "Dynamic Smart Templates: Create reusable templates with conditional logic that adjusts clauses based on deal parameters." },
        { icon: <IoListOutline />, text: "Pre-Approved Clause Library: Maintain a centralized library of legal-approved clauses for sales self-service." },
        { icon: <IoRocketOutline />, text: "Automated Data Population: Link templates to CRM or data sources to automatically populate deal terms." },
        { icon: <IoGitCompareOutline />, text: "Version-Controlled Templates: Track every change to master templates and ensure the use of the latest version." }
      ]
    },
    {
      icon: <IoKeyOutline />,
      title: "Multi-Tier User Governance & RBAC",
      description: "Manage your entire organization with precision. Define exactly who can see, edit, and sign every agreement.",
      features: [
        { icon: <IoPeopleOutline />, text: "Customizable User Roles: Create tailored roles for Legal, Sales, HR, and Finance teams." },
        { icon: <IoLockClosedOutline />, text: "Granular Document Permissions: Set access at the individual folder or document level." },
        { icon: <IoShieldOutline />, text: "Enterprise SSO & Identity: Seamlessly integrate with Okta, Azure AD, and SAML providers." },
        { icon: <IoListOutline />, text: "Activity Auditing: Track every user action—from views and downloads to edits and signatures." }
      ]
    }
  ];

  const integrations = [
    { name: "Google Drive", icon: "GD" },
    { name: "Dropbox", icon: "DB" },
    { name: "Box", icon: "BX" },
    { name: "OneDrive", icon: "OD" }
  ];

  return (
    <div className="product-page">
      {/* Hero Section */}
      <section className="product-hero">
        <div className="guest-container">
          <div className="hero-content text-center">
            <span className="badge-ai">ALL-IN-ONE PLATFORM</span>
            <h1 className="hero-heading">
              The All-in-One Platform for <span className="text-primary-blue">Smart Contract Management</span>
            </h1>
            <p className="hero-description mx-auto">
              From creation and negotiation to legally binding e-Signatures and secure archiving—manage the entire contract lifecycle in one unified, AI-powered workspace.
            </p>
            <div className="hero-buttons justify-content-center">
              <NavLink to="/sign-up" className="btn-signup-res">
                START FREE TRIAL <IoArrowForwardOutline className="arrow-icon" />
              </NavLink>
              <button className="btn-demo">
                BOOK A DEMO
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Core Modules Section */}
      <section className="product-modules">
        <div className="guest-container">
          <div className="guest-section-header">
            <h2 className="guest-sectin-title">Everything you need to <span className="text-primary-blue">manage contracts</span></h2>
            <p className="guest-section-description">
              Powerful tools designed to streamline your legal operations, accelerate deal cycles, and minimize organizational risk.
            </p>
          </div>

          <div className="modules-grid">
            {coreModules.map((module, idx) => (
              <div key={idx} className="module-card">
                <div className="module-header">
                  <div className="module-icon-main">{module.icon}</div>
                  <h3 className="module-title">{module.title}</h3>
                </div>
                <p className="module-desc">{module.description}</p>
                <div className="module-features-list">
                  {module.features.map((feature, fIdx) => (
                    <div key={fIdx} className="module-feature-item">
                      <div className="feature-icon">{feature.icon}</div>
                      <span className="feature-text">{feature.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Dashboard Showcase Section */}
      <section className="product-dashboard-showcase">
        <div className="guest-container">
          <div className="dashboard-grid">
            <div className="dashboard-content">
              <span className="badge-pill">DASHBOARD & ANALYTICS</span>
              <h2 className="guest-sectin-title">Gain a <span className="text-primary-blue">bird's-eye view</span> of your entire contract landscape</h2>
              <p className="guest-section-description">Powerful administrative tools to monitor progress, identify bottlenecks, and ensure 100% compliance.</p>
              
              <div className="dashboard-features">
                <div className="dash-feat-item">
                  <div className="dash-feat-icon"><IoGridOutline /></div>
                  <div className="dash-feat-text">
                    <h4>Unified Command Center</h4>
                    <p>Monitor all active, pending, and completed contracts from a single, intuitive dashboard.</p>
                  </div>
                </div>
                <div className="dash-feat-item">
                  <div className="dash-feat-icon"><IoStatsChartOutline /></div>
                  <div className="dash-feat-text">
                    <h4>Real-Time Tracking</h4>
                    <p>See exactly who has signed and who is causing a bottleneck with live status updates.</p>
                  </div>
                </div>
                <div className="dash-feat-item">
                  <div className="dash-feat-icon"><IoNotificationsOutline /></div>
                  <div className="dash-feat-text">
                    <h4>Smart Alerts</h4>
                    <p>Get automated "at-risk" alerts for upcoming renewals and termination windows.</p>
                  </div>
                </div>
                <div className="dash-feat-item">
                  <div className="dash-feat-icon"><IoPieChartOutline /></div>
                  <div className="dash-feat-text">
                    <h4>Custom Reporting</h4>
                    <p>Generate detailed reports on turnaround times, team productivity, and vendor compliance.</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="dashboard-visual">
              <div className="mockup-frame">
                <img src={dashboardImg} alt="DocuSafe Dashboard Mockup" className="dashboard-mockup-img" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Integrations Section */}
      <section className="product-integrations">
        <div className="guest-container text-center">
          <h2 className="integration-title">Native Integrations</h2>
          <p className="integration-subtitle">Connect DocuSafe with the tools you already use for seamless document flow</p>
          <div className="integration-logos">
            {integrations.map((int, idx) => (
              <div key={idx} className="integration-logo-card">
                <div className={`int-icon ${int.icon.toLowerCase()}`}>
                  <IoCloudOutline />
                </div>
                <span className="int-name">{int.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Security Section */}
      <section className="product-security">
        <div className="guest-container">
          <div className="security-card">
            <div className="security-content">
              <h2 className="security-title">Bank-Grade Security</h2>
              <p className="security-desc">Your legal data is protected by the highest industry-standard encryption and compliance frameworks.</p>
              <ul className="security-list">
                <li><IoShieldCheckmarkOutline /> <span>SOC 2 Type II Compliant Infrastructure</span></li>
                <li><IoLockClosedOutline /> <span>256-bit AES Encryption at rest and TLS 1.2+ in transit</span></li>
                <li><IoFingerPrintOutline /> <span>Enterprise-grade Multi-Factor Authentication (MFA)</span></li>
              </ul>
            </div>
            <div className="security-image-placeholder">
              <IoShieldCheckmarkOutline className="large-shield" />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="product-cta">
        <div className="guest-container">
          <div className="cta-wrapper">
            <h2>Ready to transform your contract workflow?</h2>
            <p>Join thousands of forward-thinking teams scaling their business with DocuSafe.ai</p>
            <div className="hero-buttons justify-content-center">
              <NavLink to="/sign-up" className="btn-signup-white">
                START FREE TRIAL
              </NavLink>
              <button className="btn-demo-white">
                REQUEST DEMO
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProductPage;
